import { StyleSheet } from 'aphrodite';
import { SPACE, THEME } from '../../components-v2/Theme/theme';

export const styleSheet = StyleSheet.create({
	detailRoot: {
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		minHeight: 0,
	},
	detailTabRoot: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		minHeight: 0,
	},
	// Flex-sizing only, no overflow — the outer <Column scrollable> in index.tsx is the single scroll
	// owner for this page. An overflowY:auto here would nest a second nearly-full-height scroll region
	// inside that one, which previously stranded the details form's Save/Cancel row inside its own
	// undersized inner scrollbar instead of flowing to the bottom of the page.
	//
	// Setting `display` unconditionally also fights Radix's inactive-panel `hidden` attribute — the
	// native `[hidden] { display: none }` UA rule loses to any author rule that also sets `display`, so
	// an inactive TabsContent can stay laid out (and visible) instead of collapsing, pushing every tab
	// after it down the page. The `data-state="inactive"` override below wins that fight explicitly.
	detailTabContentFill: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		minHeight: 0,
		':is([data-state="inactive"])': {
			display: 'none',
		},
	},
	subtitle: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		margin: `${THEME.spacing.xs}px 0 0`,
	},
	databoard: {
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		paddingBottom: THEME.spacing.md,
		paddingTop: THEME.spacing.sm,
		width: '100%',
	},
	actions: {
		gap: THEME.spacing.md,
		justifyContent: 'space-between',
		marginBottom: THEME.spacing.md,
	},
	filters: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.sm,
	},
	filterSeparator: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
	},
	dateFilterTrigger: {
		backgroundColor: THEME.colors.background.light,
	},
	searchInput: {
		backgroundColor: THEME.colors.background.light,
		minWidth: 220,
	},
	sortFilterTrigger: {
		backgroundColor: THEME.colors.background.light,
	},
	sortTrigger: {
		alignItems: 'center',
		color: THEME.colors.text.main,
		display: 'flex',
		padding: 0,
		':hover': {
			textDecoration: 'none',
		},
	},
	sortIcon: {
		display: 'flex',
		marginLeft: THEME.spacing.xs,
	},
	sortIconAscending: {
		transform: 'rotate(180deg)',
	},
	emptyState: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		justifyContent: 'center',
		padding: THEME.spacing.xxl,
		textAlign: 'center',
	},
	emptyIllustration: {
		height: 'auto',
		marginBottom: THEME.spacing.sm,
		maxWidth: 320,
		width: '100%',
	},
	emptyTitle: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
		margin: 0,
	},
	emptyText: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		margin: 0,
		maxWidth: 380,
	},
	emptyActions: {
		marginTop: THEME.spacing.md,
	},
	row: {
		cursor: 'pointer',
	},
	mutedCell: {
		color: THEME.colors.text.light,
	},
	form: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.lg,
		maxWidth: 640,
		paddingBottom: THEME.spacing.md,
		paddingTop: THEME.spacing.sm,
	},
	editorWrapper: {
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		overflow: 'hidden',
	},
	dateRow: {
		display: 'flex',
		gap: THEME.spacing.md,
	},
	dateField: {
		flex: 1,
	},
	formActions: {
		display: 'flex',
		gap: THEME.spacing.sm,
		marginTop: THEME.spacing.sm,
	},
	inlineCheckbox: {
		alignItems: 'center',
		display: 'flex',
		gap: THEME.spacing.sm,
	},
	readOnlyNote: {
		alignItems: 'center',
		backgroundColor: THEME.colors.info.light,
		borderLeft: `4px solid ${THEME.colors.info.main}`,
		borderRadius: THEME.common.borderRadius,
		color: THEME.colors.text.main,
		display: 'flex',
		fontSize: THEME.typography.fontSizes.sm,
		gap: THEME.spacing.md,
		justifyContent: 'space-between',
		marginBottom: THEME.spacing.md,
		padding: THEME.spacing.md,
		[THEME.queries.sm]: {
			alignItems: 'flex-start',
			flexDirection: 'column',
		},
	},
	readOnlyCard: {
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		margin: 0,
		maxWidth: 720,
	},
	readOnlyRow: {
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		display: 'grid',
		gap: THEME.spacing.md,
		gridTemplateColumns: '160px 1fr',
		padding: THEME.spacing.md,
		':last-child': {
			borderBottom: 'none',
		},
		[THEME.queries.sm]: {
			gap: THEME.spacing.xs,
			gridTemplateColumns: '1fr',
		},
	},
	readOnlyLabel: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	readOnlyValue: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.md,
		margin: 0,
		minWidth: 0,
		overflowWrap: 'break-word',
	},
	tableOverrides: {
		borderBottomLeftRadius: 0,
		borderBottomRightRadius: 0,
		paddingBottom: 0,
		paddingTop: 0,
	},
	tableRow: {
		backgroundColor: THEME.colors.background.light,
	},
	loadingState: {
		alignItems: 'center',
		display: 'flex',
		flex: 1,
		justifyContent: 'center',
		padding: THEME.spacing.xxl,
	},
	loadMore: {
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'center',
		padding: THEME.spacing.md,
	},
	tilesGrid: {
		boxSizing: 'border-box',
		containerType: 'inline-size',
		display: 'grid',
		gap: THEME.spacing.md,
		gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
		minWidth: 0,
		paddingBlock: THEME.spacing.md,
		width: '100%',
	},
	tile: {
		gridColumn: 'span 4',
	},
	tilesFootnoteLink: {
		color: THEME.colors.primary.main,
	},
	dataBoardCallout: {
		alignItems: 'center',
		backgroundColor: THEME.colors.hover.light,
		border: `1px solid ${THEME.colors.hover.main}`,
		borderRadius: THEME.common.borderRadius,
		boxSizing: 'border-box',
		display: 'flex',
		gap: SPACE[16],
		padding: SPACE[16],
	},
	dataBoardCalloutIcon: {
		flexShrink: 0,
	},
	dataBoardCalloutTitle: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
		marginBottom: SPACE[4],
	},
	dataBoardCalloutText: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
		margin: 0,
	},
	dataBoardCalloutLink: {
		color: THEME.colors.primary.main,
		fontWeight: THEME.typography.fontWeights.bold,
		textDecoration: 'none',
		whiteSpace: 'nowrap',
		':hover': {
			textDecoration: 'underline',
		},
	},
	dataBoardCalloutChevron: {
		height: 16,
		width: 16,
	},
	hugTable: {
		height: 'auto',
	},
	contactNameButton: {
		padding: 0,
	},
	tileTitleWithHelp: {
		alignItems: 'center',
		display: 'inline-flex',
		gap: THEME.spacing.xs,
		justifyContent: 'center',
	},
	tileHelpContent: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.standard,
		margin: 0,
		maxWidth: 280,
		textAlign: 'left',
	},
	dayPickerTrigger: {
		height: 36,
		minWidth: 200,
		backgroundColor: THEME.colors.background.light,
	},
	dayPickerLabel: {
		flexGrow: 1,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	dayPickerLabelMuted: {
		color: THEME.colors.text.light,
	},
	dayPickerPopover: {
		padding: 0,
	},
});
